import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonItem, IonLabel, IonText } from '@ionic/react';
import { DrawerSectionHeading } from '../../components/DrawerSectionHeading';
import { PrivateCheckbox } from '../../components/PrivateCheckbox';
import { Spinner } from '../../components/Spinner';
import { ChevronDownIcon } from '../../theme/Icons';
import { IStructuredRecordDescriptor } from './StructuredRecordDescriptor';

const useStyles = createUseStyles({
    listWrapper: {
        paddingLeft: 16,
        marginTop: -4,

        // descendant (not direct-child) so rows nested inside collapsible sections get the
        // same zero start-padding -- otherwise sectioned meds rows sit 16px further right
        '& ion-item::part(native)': {
            paddingLeft: 0
        }
    },

    // vertical breathing room above a labelled group (e.g. "Not currently taking")
    section: {
        marginTop: 15
    },

    sectionLabel: {
        paddingLeft: 12,
        fontWeight: 500
    },

    collapsibleHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        paddingLeft: 0,
        paddingTop: 8,
        paddingBottom: 8,
        fontWeight: 500,
        color: 'var(--ion-color-medium)',
        background: 'none',
        border: 'none',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer'
    },

    chevron: {
        display: 'flex',
        transition: 'transform 0.15s ease'
    },

    // flex-centre keeps the icon vertically aligned with the (1- or 2-line) label; marginInlineEnd
    // overrides Ionic's larger default start-slot gap so the label sits closer to the icon
    icon: {
        display: 'flex',
        alignItems: 'center',
        marginInlineEnd: 12
    },

    // secondary preview line under the name (e.g. medication instructions); rendered only when
    // there's a value, so rows with none stay single-line with no reserved blank line
    subtext: {
        fontSize: 14,
        color: 'var(--ion-color-medium)',
        lineHeight: 1.3
    }
});

export interface IStructuredRecordSection<T> {
    label: string;
    records: T[];
    collapsible?: boolean;
    defaultCollapsed?: boolean;
}

export interface IStructuredRecordListProps<T> {
    descriptor: IStructuredRecordDescriptor<T>;
    records: T[];
    canAddEdit: boolean;
    isPublic: boolean;
    // when true, the heading's add button is replaced in place by a spinner (medications use this
    // while a photo is being processed into a new record); other record types never set it
    addButtonBusy?: boolean;
    onTogglePublic: () => void;
    onRowClick: (id: string) => void;
    onAdd: () => void;
    sections?: IStructuredRecordSection<T>[];
}

/**
 * Generalizes MedicationList's row/empty-state/privacy-toggle rendering. Add-button and
 * photo-import logic stay meds-specific (not generalized here) -- this only renders a plain
 * "Add {singular}" button that calls `onAdd`.
 */
export const StructuredRecordList = <T,>({
    descriptor,
    records,
    canAddEdit,
    isPublic,
    addButtonBusy,
    onTogglePublic,
    onRowClick,
    onAdd,
    sections
}: IStructuredRecordListProps<T>) => {
    const classes = useStyles();

    const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        (sections || []).forEach((section, idx) => {
            if (section.collapsible && section.defaultCollapsed) {
                initial[section.label || String(idx)] = true;
            }
        });
        return initial;
    });

    // hide the whole section (heading included) from anyone who can't edit when it's private --
    // showing a viewer an empty "No {plural}" for private medical data is misleading and unsafe.
    // Shared (public) sections stay visible to viewers.
    if (!isPublic && !canAddEdit) {
        return null;
    }

    const renderRow = (record: T, idx: number) => {
        const rawId = record[descriptor.idField];
        const id = rawId === undefined || rawId === null ? undefined : String(rawId);
        const Icon = descriptor.Icon;
        const subtext = descriptor.getSubtext?.(record);

        return (
            <IonItem
                button
                detail
                key={id || `idx-${idx}`}
                onClick={() => {
                    if (id !== undefined) {
                        onRowClick(id);
                    }
                }}
            >
                {Icon && (
                    <div slot="start" className={classes.icon}>
                        <Icon color="medium" />
                    </div>
                )}
                <IonLabel>
                    <IonText data-type="name">{descriptor.getName(record)}</IonText>
                    {subtext && <div className={classes.subtext}>{subtext}</div>}
                </IonLabel>
            </IonItem>
        );
    };

    const renderRows = (rows: T[]) => (
        <>
            {rows.length === 0 && (
                <IonText color="medium" style={{paddingLeft: 12}}>
                    No {descriptor.plural.toLowerCase()}
                </IonText>
            )}

            {rows.map((record, idx) => renderRow(record, idx))}
        </>
    );

    const renderSection = (section: IStructuredRecordSection<T>, idx: number) => {
        const key = section.label || String(idx);
        const isCollapsed = Boolean(section.collapsible && collapsed[key]);

        return (
            <div key={key} data-type="structured-record-section" data-section-label={section.label} className={section.label ? classes.section : undefined}>
                {section.label && (section.collapsible ? (
                    <button
                        type="button"
                        className={classes.collapsibleHeader}
                        aria-expanded={!isCollapsed}
                        data-type="section-toggle"
                        onClick={() => setCollapsed(c => ({ ...c, [key]: !isCollapsed }))}
                    >
                        <span className={classes.chevron} style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                            <ChevronDownIcon color="medium" customSize={14} />
                        </span>
                        <span>{section.label}</span>
                    </button>
                ) : (
                    <IonText color="medium" className={classes.sectionLabel}>{section.label}</IonText>
                ))}
                {!isCollapsed && renderRows(section.records)}
            </div>
        );
    };

    return (
        <div data-type={`${descriptor.routeSegment}-list`}>
            <DrawerSectionHeading text={descriptor.plural}>
                {canAddEdit && (addButtonBusy ? (
                    // swap the add button for a spinner in the same slot while a photo is being
                    // processed into a new record (matches the pre-generalization in-place swap)
                    <div style={{paddingRight: 16}}>
                        <Spinner size="small" />
                    </div>
                ) : (
                    <IonButton
                        fill="clear"
                        color="primary"
                        data-id={`add-${descriptor.routeSegment}-button`}
                        onClick={onAdd}
                    >
                        + Add {descriptor.singular}
                    </IonButton>
                ))}
            </DrawerSectionHeading>

            {canAddEdit && (
                <div style={{display: 'flex', justifyContent: 'space-between', marginTop: -8}}>
                    <PrivateCheckbox
                        data-id={`${descriptor.routeSegment}-visibility-checkbox`}
                        checked={!isPublic}
                        onChange={() => onTogglePublic()}
                    />
                </div>
            )}

            <div className={classes.listWrapper}>
                {sections ? sections.map((section, idx) => renderSection(section, idx)) : renderRows(records)}
            </div>
        </div>
    );
};
